import { useState, useEffect, useRef, useCallback } from "react";

const COLORS = {
  bg: "#0a0e1a",
  bgCard: "#111827",
  bgCardAlt: "#1a2236",
  border: "#1e293b",
  borderActive: "#3b82f6",
  text: "#e2e8f0",
  textMuted: "#94a3b8",
  textDim: "#64748b",
  accent: "#3b82f6",
  accentGlow: "rgba(59,130,246,0.15)",
  success: "#22c55e",
  successGlow: "rgba(34,197,94,0.15)",
  warning: "#f59e0b",
  warningGlow: "rgba(245,158,11,0.15)",
  danger: "#ef4444",
  dangerGlow: "rgba(239,68,68,0.15)",
  purple: "#a78bfa",
  purpleGlow: "rgba(167,139,250,0.15)",
  cyan: "#22d3ee",
};

const FONT = `'JetBrains Mono', 'Fira Code', 'SF Mono', monospace`;
const FONT_BODY = `'DM Sans', 'Segoe UI', system-ui, sans-serif`;

const layout = {
  app: { background: COLORS.bg, minHeight: "100vh", color: COLORS.text, fontFamily: FONT_BODY, fontSize: 14 },
  sidebar: { width: 240, background: COLORS.bgCard, borderRight: `1px solid ${COLORS.border}`, height: "calc(100vh - 70px)", position: "fixed", top: 70, left: 0, overflowY: "auto", zIndex: 100 },
  main: { marginLeft: 240, minHeight: "100vh", padding: "32px 40px", maxWidth: 1000 },
  navItem: (active) => ({ display: "flex", alignItems: "center", gap: 10, padding: "10px 16px", cursor: "pointer", borderRadius: 8, margin: "2px 8px", background: active ? COLORS.accentGlow : "transparent", color: active ? COLORS.accent : COLORS.textMuted, fontWeight: active ? 600 : 400, fontSize: 13, transition: "all .18s", borderLeft: active ? `3px solid ${COLORS.accent}` : "3px solid transparent" }),
  badge: (col) => ({ display: "inline-block", padding: "2px 10px", borderRadius: 12, fontSize: 11, fontWeight: 700, background: col + "22", color: col, border: `1px solid ${col}44`, letterSpacing: 0.3 }),
};

// --- SECTION 1: CONCEPT BREAKDOWN ---
function ConceptSection() {
  const [activeCard, setActiveCard] = useState(null);
  const concepts = [
    {
      icon: "🏗️",
      title: "What IS a Cluster Upgrade?",
      color: COLORS.accent,
      glow: COLORS.accentGlow,
      content: `A Kubernetes cluster has many components running at specific versions. An "upgrade" means updating these components from an older version (e.g., v1.29) to a newer one (e.g., v1.30) — one piece at a time, without breaking your running apps.`,
    },
    {
      icon: "🧠",
      title: "Control Plane First",
      color: COLORS.purple,
      glow: COLORS.purpleGlow,
      content: `The control plane (API Server, Controller Manager, Scheduler, etcd) is the "brain" of your cluster. It MUST be upgraded before any worker nodes. Think of it as updating headquarters before updating the branch offices.`,
    },
    {
      icon: "⚙️",
      title: "Worker Nodes Second",
      color: COLORS.success,
      glow: COLORS.successGlow,
      content: `After the control plane is upgraded, you upgrade worker nodes ONE BY ONE. Each node is drained (pods moved away), upgraded, then brought back. This ensures zero downtime for your apps.`,
    },
    {
      icon: "📏",
      title: "The Version Skew Rule",
      color: COLORS.warning,
      glow: COLORS.warningGlow,
      content: `Kubelet (on workers) can be at most 2 minor versions BEHIND the API Server. You can't skip versions — you must go v1.29 → v1.30 → v1.31, never v1.29 → v1.31 directly.`,
    },
  ];

  return (
    <div style={{ marginBottom: 40 }}>
      <h2 style={{ fontFamily: FONT, fontSize: 18, color: COLORS.cyan, marginBottom: 6, letterSpacing: 2 }}>
        01 — THE BIG PICTURE
      </h2>
      <p style={{ fontFamily: FONT_BODY, color: COLORS.textMuted, fontSize: 14, marginBottom: 20, lineHeight: 1.6 }}>
        Before we dive into commands, let's understand <em>what</em> we're doing and <em>why</em>.
      </p>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        {concepts.map((c, i) => (
          <div
            key={i}
            onClick={() => setActiveCard(activeCard === i ? null : i)}
            style={{
              background: activeCard === i ? c.glow : COLORS.bgCard,
              border: `1px solid ${activeCard === i ? c.color : COLORS.border}`,
              borderRadius: 10,
              padding: 16,
              cursor: "pointer",
              transition: "all 0.3s",
              transform: activeCard === i ? "scale(1.02)" : "scale(1)",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
              <span style={{ fontSize: 22 }}>{c.icon}</span>
              <span style={{ fontFamily: FONT, fontSize: 13, color: c.color, fontWeight: 700 }}>{c.title}</span>
            </div>
            <p style={{
              fontFamily: FONT_BODY, fontSize: 12.5, color: COLORS.textMuted, lineHeight: 1.65,
              maxHeight: activeCard === i ? 200 : 0, overflow: "hidden",
              transition: "max-height 0.4s ease", margin: 0,
            }}>
              {c.content}
            </p>
            {activeCard !== i && (
              <span style={{ fontFamily: FONT, fontSize: 10, color: COLORS.textDim }}>tap to expand ▾</span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// --- SECTION 2: ANALOGY ---
function AnalogySection() {
  const [step, setStep] = useState(0);
  const analogySteps = [
    {
      emoji: "🏨",
      title: "The Hotel Analogy",
      text: "Imagine your Kubernetes cluster is a HOTEL. The control plane is the front desk & management office. Worker nodes are the hotel floors. Pods are the guests in rooms.",
      highlight: COLORS.accent,
    },
    {
      emoji: "📋",
      title: "Step 1: Upgrade the Front Desk",
      text: "First, you upgrade the management office (control plane). The guests (pods) don't even notice — they're happily in their rooms. The hotel stays OPEN the whole time.",
      highlight: COLORS.purple,
    },
    {
      emoji: "🚧",
      title: "Step 2: Renovate Floor by Floor",
      text: "Now you renovate one floor (worker node) at a time. Before renovating, you MOVE all guests (drain pods) from that floor to other floors. No guest is left without a room!",
      highlight: COLORS.warning,
    },
    {
      emoji: "🔄",
      title: "Step 3: Guests Return",
      text: "After a floor is renovated (node upgraded), guests (pods) can be scheduled back. You repeat this floor-by-floor until the entire hotel is upgraded. Zero guest complaints!",
      highlight: COLORS.success,
    },
  ];
  const s = analogySteps[step];

  return (
    <div style={{ marginBottom: 40 }}>
      <h2 style={{ fontFamily: FONT, fontSize: 18, color: COLORS.cyan, marginBottom: 6, letterSpacing: 2 }}>
        02 — THE HOTEL ANALOGY
      </h2>
      <p style={{ fontFamily: FONT_BODY, color: COLORS.textMuted, fontSize: 14, marginBottom: 16, lineHeight: 1.6 }}>
        A cluster upgrade is like renovating a hotel — floor by floor — without kicking any guests out.
      </p>
      <div style={{
        background: COLORS.bgCard, border: `1px solid ${s.highlight}`, borderRadius: 12,
        padding: 24, position: "relative", overflow: "hidden",
      }}>
        <div style={{
          position: "absolute", top: 0, left: 0, width: `${((step + 1) / analogySteps.length) * 100}%`,
          height: 3, background: s.highlight, transition: "width 0.5s ease", borderRadius: 2,
        }} />
        <div style={{ fontSize: 48, marginBottom: 12, textAlign: "center" }}>{s.emoji}</div>
        <h3 style={{ fontFamily: FONT, fontSize: 15, color: s.highlight, textAlign: "center", marginBottom: 10 }}>{s.title}</h3>
        <p style={{ fontFamily: FONT_BODY, fontSize: 13.5, color: COLORS.text, textAlign: "center", lineHeight: 1.7, maxWidth: 500, margin: "0 auto 16px" }}>
          {s.text}
        </p>
        <div style={{ display: "flex", justifyContent: "center", gap: 8 }}>
          {analogySteps.map((_, i) => (
            <button key={i} onClick={() => setStep(i)} style={{
              width: i === step ? 28 : 10, height: 10, borderRadius: 5, border: "none", cursor: "pointer",
              background: i === step ? s.highlight : COLORS.border, transition: "all 0.3s",
            }} />
          ))}
        </div>
      </div>
    </div>
  );
}

// --- SECTION 3: ANIMATED UPGRADE SIMULATOR ---
const SIM_PHASES = [
  { id: "idle", label: "Cluster Running (v1.29)", duration: 2000 },
  { id: "plan", label: "kubeadm upgrade plan", duration: 2500 },
  { id: "cp-upgrade", label: "Upgrading Control Plane → v1.30", duration: 3500 },
  { id: "cp-done", label: "Control Plane ✓ v1.30", duration: 1500 },
  { id: "drain-w1", label: "Draining Worker-1 (moving pods)", duration: 3000 },
  { id: "upgrade-w1", label: "Upgrading Worker-1 → v1.30", duration: 2500 },
  { id: "uncordon-w1", label: "Uncordon Worker-1 (back online)", duration: 1500 },
  { id: "drain-w2", label: "Draining Worker-2 (moving pods)", duration: 3000 },
  { id: "upgrade-w2", label: "Upgrading Worker-2 → v1.30", duration: 2500 },
  { id: "uncordon-w2", label: "Uncordon Worker-2 (back online)", duration: 1500 },
  { id: "done", label: "Cluster Fully Upgraded to v1.30 🎉", duration: 3000 },
];

function getNodeState(phaseId) {
  const cp = { version: "v1.29", status: "running", color: COLORS.accent };
  const w1 = { version: "v1.29", status: "running", color: COLORS.success, pods: ["🟢","🟢","🟢"] };
  const w2 = { version: "v1.29", status: "running", color: COLORS.success, pods: ["🟢","🟢","🟢"] };

  const states = {
    idle: { cp, w1, w2 },
    plan: { cp: { ...cp, status: "planning", color: COLORS.warning }, w1, w2 },
    "cp-upgrade": { cp: { ...cp, status: "upgrading ⏳", color: COLORS.warning }, w1, w2 },
    "cp-done": { cp: { version: "v1.30", status: "upgraded ✓", color: COLORS.success }, w1, w2 },
    "drain-w1": {
      cp: { version: "v1.30", status: "running", color: COLORS.success },
      w1: { version: "v1.29", status: "draining 🚚", color: COLORS.warning, pods: ["⬜"] },
      w2: { ...w2, pods: ["🟢","🟢","🟢","🟡","🟡","🟡"] },
    },
    "upgrade-w1": {
      cp: { version: "v1.30", status: "running", color: COLORS.success },
      w1: { version: "v1.29→30", status: "upgrading ⏳", color: COLORS.warning, pods: [] },
      w2: { ...w2, pods: ["🟢","🟢","🟢","🟡","🟡","🟡"] },
    },
    "uncordon-w1": {
      cp: { version: "v1.30", status: "running", color: COLORS.success },
      w1: { version: "v1.30", status: "ready ✓", color: COLORS.success, pods: ["🟢","🟢","🟢"] },
      w2: { ...w2, pods: ["🟢","🟢","🟢"] },
    },
    "drain-w2": {
      cp: { version: "v1.30", status: "running", color: COLORS.success },
      w1: { version: "v1.30", status: "running", color: COLORS.success, pods: ["🟢","🟢","🟢","🟡","🟡","🟡"] },
      w2: { version: "v1.29", status: "draining 🚚", color: COLORS.warning, pods: ["⬜"] },
    },
    "upgrade-w2": {
      cp: { version: "v1.30", status: "running", color: COLORS.success },
      w1: { version: "v1.30", status: "running", color: COLORS.success, pods: ["🟢","🟢","🟢","🟡","🟡","🟡"] },
      w2: { version: "v1.29→30", status: "upgrading ⏳", color: COLORS.warning, pods: [] },
    },
    "uncordon-w2": {
      cp: { version: "v1.30", status: "running", color: COLORS.success },
      w1: { version: "v1.30", status: "running", color: COLORS.success, pods: ["🟢","🟢","🟢"] },
      w2: { version: "v1.30", status: "ready ✓", color: COLORS.success, pods: ["🟢","🟢","🟢"] },
    },
    done: {
      cp: { version: "v1.30", status: "running ✓", color: COLORS.success },
      w1: { version: "v1.30", status: "running ✓", color: COLORS.success, pods: ["🟢","🟢","🟢"] },
      w2: { version: "v1.30", status: "running ✓", color: COLORS.success, pods: ["🟢","🟢","🟢"] },
    },
  };
  return states[phaseId] || states.idle;
}

function NodeBox({ label, version, status, color, pods, icon }) {
  return (
    <div style={{
      background: COLORS.bgCardAlt, border: `2px solid ${color}`, borderRadius: 10,
      padding: 14, minWidth: 150, textAlign: "center",
      boxShadow: `0 0 20px ${color}33`, transition: "all 0.5s",
    }}>
      <div style={{ fontSize: 26, marginBottom: 4 }}>{icon}</div>
      <div style={{ fontFamily: FONT, fontSize: 12, color, fontWeight: 700, marginBottom: 2 }}>{label}</div>
      <div style={{ fontFamily: FONT, fontSize: 11, color: COLORS.text }}>{version}</div>
      <div style={{ fontFamily: FONT, fontSize: 10, color: COLORS.textMuted, marginBottom: 6 }}>{status}</div>
      {pods && (
        <div style={{ display: "flex", justifyContent: "center", gap: 3, flexWrap: "wrap", minHeight: 20 }}>
          {pods.map((p, i) => <span key={i} style={{ fontSize: 12, transition: "all 0.3s" }}>{p}</span>)}
        </div>
      )}
    </div>
  );
}

function SimulatorSection() {
  const [phaseIdx, setPhaseIdx] = useState(0);
  const [running, setRunning] = useState(false);
  const [progress, setProgress] = useState(0);
  const timerRef = useRef(null);
  const progressRef = useRef(null);

  const phase = SIM_PHASES[phaseIdx];
  const ns = getNodeState(phase.id);

  const stopSim = useCallback(() => {
    setRunning(false);
    clearTimeout(timerRef.current);
    clearInterval(progressRef.current);
  }, []);

  const advancePhase = useCallback((idx) => {
    if (idx >= SIM_PHASES.length) { stopSim(); return; }
    setPhaseIdx(idx);
    setProgress(0);
    const dur = SIM_PHASES[idx].duration;
    const tick = 50;
    let elapsed = 0;
    progressRef.current = setInterval(() => {
      elapsed += tick;
      setProgress(Math.min((elapsed / dur) * 100, 100));
    }, tick);
    timerRef.current = setTimeout(() => {
      clearInterval(progressRef.current);
      advancePhase(idx + 1);
    }, dur);
  }, [stopSim]);

  const startSim = () => { setPhaseIdx(0); setRunning(true); advancePhase(0); };
  const resetSim = () => { stopSim(); setPhaseIdx(0); setProgress(0); };

  useEffect(() => () => { clearTimeout(timerRef.current); clearInterval(progressRef.current); }, []);

  const command = {
    idle: "$ kubectl get nodes",
    plan: "$ kubeadm upgrade plan",
    "cp-upgrade": "$ kubeadm upgrade apply v1.30.0",
    "cp-done": "# Control plane upgraded!",
    "drain-w1": "$ kubectl drain worker-1 --ignore-daemonsets",
    "upgrade-w1": "$ apt install kubelet=1.30.0 && systemctl restart kubelet",
    "uncordon-w1": "$ kubectl uncordon worker-1",
    "drain-w2": "$ kubectl drain worker-2 --ignore-daemonsets",
    "upgrade-w2": "$ apt install kubelet=1.30.0 && systemctl restart kubelet",
    "uncordon-w2": "$ kubectl uncordon worker-2",
    done: "# 🎉 All nodes running v1.30!",
  }[phase.id];

  return (
    <div style={{ marginBottom: 40 }}>
      <h2 style={{ fontFamily: FONT, fontSize: 18, color: COLORS.cyan, marginBottom: 6, letterSpacing: 2 }}>
        03 — LIVE UPGRADE SIMULATOR
      </h2>
      <p style={{ fontFamily: FONT_BODY, color: COLORS.textMuted, fontSize: 14, marginBottom: 16, lineHeight: 1.6 }}>
        Watch pods move between nodes as the cluster upgrades from <strong style={{color:COLORS.warning}}>v1.29</strong> → <strong style={{color:COLORS.success}}>v1.30</strong>, one component at a time.
      </p>

      <div style={{ background: COLORS.bgCard, border: `1px solid ${COLORS.border}`, borderRadius: 12, padding: 20, overflow: "hidden" }}>
        {/* Phase label + progress */}
        <div style={{ marginBottom: 14 }}>
          <div style={{ fontFamily: FONT, fontSize: 13, color: COLORS.text, marginBottom: 6, minHeight: 20 }}>
            {phase.label}
          </div>
          <div style={{ height: 4, background: COLORS.border, borderRadius: 2, overflow: "hidden" }}>
            <div style={{ width: `${progress}%`, height: "100%", background: COLORS.cyan, borderRadius: 2, transition: "width 0.05s linear" }} />
          </div>
        </div>

        {/* Terminal */}
        <div style={{
          background: "#000", borderRadius: 8, padding: "10px 14px", marginBottom: 16,
          fontFamily: FONT, fontSize: 12, color: COLORS.success, letterSpacing: 0.5,
          border: `1px solid ${COLORS.border}`,
        }}>
          {command}
          {running && <span style={{ animation: "blink 1s step-end infinite" }}>▌</span>}
        </div>

        {/* Cluster viz */}
        <div style={{ display: "flex", justifyContent: "center", gap: 16, flexWrap: "wrap", marginBottom: 16 }}>
          <NodeBox label="Control Plane" icon="🧠" {...ns.cp} />
          <NodeBox label="Worker-1" icon="⚙️" {...ns.w1} />
          <NodeBox label="Worker-2" icon="⚙️" {...ns.w2} />
        </div>

        {/* Step counter */}
        <div style={{ display: "flex", justifyContent: "center", gap: 5, marginBottom: 14 }}>
          {SIM_PHASES.map((_, i) => (
            <div key={i} style={{
              width: 8, height: 8, borderRadius: 4,
              background: i < phaseIdx ? COLORS.success : i === phaseIdx ? COLORS.cyan : COLORS.border,
              transition: "background 0.3s",
            }} />
          ))}
        </div>

        {/* Controls */}
        <div style={{ display: "flex", justifyContent: "center", gap: 10 }}>
          <button onClick={running ? stopSim : startSim} style={{
            fontFamily: FONT, fontSize: 13, padding: "8px 22px", borderRadius: 6,
            border: "none", cursor: "pointer", fontWeight: 700,
            background: running ? COLORS.danger : COLORS.accent,
            color: "#fff",
          }}>
            {running ? "⏸ Pause" : phaseIdx > 0 && !running ? "▶ Resume" : "▶ Start Upgrade"}
          </button>
          <button onClick={resetSim} style={{
            fontFamily: FONT, fontSize: 13, padding: "8px 16px", borderRadius: 6,
            border: `1px solid ${COLORS.border}`, cursor: "pointer",
            background: "transparent", color: COLORS.textMuted,
          }}>
            ↺ Reset
          </button>
        </div>
      </div>

      <style>{`@keyframes blink { 50% { opacity: 0; } }`}</style>
    </div>
  );
}

// --- SECTION 4: STEP-BY-STEP PROCESS ---
function ProcessSection() {
  const [openStep, setOpenStep] = useState(null);
  const steps = [
    {
      num: "01", title: "Check Current Version", icon: "🔍", color: COLORS.accent,
      cmd: "kubectl get nodes\nkubeadm version",
      explain: "First, see what version everything is running. Like checking the hotel's current renovation status before planning.",
    },
    {
      num: "02", title: "Plan the Upgrade", icon: "📋", color: COLORS.purple,
      cmd: "kubeadm upgrade plan",
      explain: "This command shows you what versions are available and if the upgrade is safe. It's like the architect showing the blueprint before construction begins.",
    },
    {
      num: "03", title: "Upgrade kubeadm on Master", icon: "📦", color: COLORS.cyan,
      cmd: "apt-get update\napt-get install -y kubeadm=1.30.0-*",
      explain: "Update the kubeadm tool itself first. You need the new version of the renovation toolkit before you can use it to renovate!",
    },
    {
      num: "04", title: "Apply Control Plane Upgrade", icon: "🧠", color: COLORS.warning,
      cmd: "kubeadm upgrade apply v1.30.0",
      explain: "This upgrades all control plane components: API Server, Controller Manager, Scheduler, and kube-proxy config. The brain of the hotel gets its makeover.",
    },
    {
      num: "05", title: "Upgrade kubelet & kubectl on Master", icon: "🔧", color: COLORS.success,
      cmd: "apt-get install -y kubelet=1.30.0-* kubectl=1.30.0-*\nsystemctl daemon-reload\nsystemctl restart kubelet",
      explain: "Even the master node runs a kubelet. Update it and restart the service. Like replacing the front desk phone system after renovating the office.",
    },
    {
      num: "06", title: "Drain a Worker Node", icon: "🚚", color: COLORS.danger,
      cmd: "kubectl drain worker-1 \\\n  --ignore-daemonsets \\\n  --delete-emptydir-data",
      explain: "Move all pods OFF this node. The node is marked unschedulable (cordoned). It's like moving all hotel guests to other floors before renovating that floor.",
    },
    {
      num: "07", title: "Upgrade Worker Node", icon: "⬆️", color: COLORS.warning,
      cmd: "# SSH into worker-1\napt-get update\napt-get install -y kubeadm=1.30.0-*\nkubeadm upgrade node\napt-get install -y kubelet=1.30.0-*\nsystemctl daemon-reload\nsystemctl restart kubelet",
      explain: "SSH into the worker, upgrade kubeadm, run the node upgrade, then upgrade kubelet. The floor is being renovated now!",
    },
    {
      num: "08", title: "Uncordon the Worker", icon: "✅", color: COLORS.success,
      cmd: "kubectl uncordon worker-1",
      explain: "Mark the node as schedulable again. Pods can now be placed back on it. The renovated floor is open for guests again!",
    },
    {
      num: "09", title: "Repeat for All Workers", icon: "🔄", color: COLORS.purple,
      cmd: "# Repeat steps 06-08 for worker-2, worker-3, etc.",
      explain: "Do the same drain → upgrade → uncordon cycle for every worker node. One floor at a time until the entire hotel is renovated.",
    },
    {
      num: "10", title: "Verify the Upgrade", icon: "🎯", color: COLORS.cyan,
      cmd: "kubectl get nodes\n# All nodes should show v1.30",
      explain: "Final check — every node should now report the new version. The grand re-opening! All floors renovated, all guests happy.",
    },
  ];

  return (
    <div style={{ marginBottom: 40 }}>
      <h2 style={{ fontFamily: FONT, fontSize: 18, color: COLORS.cyan, marginBottom: 6, letterSpacing: 2 }}>
        04 — STEP-BY-STEP PROCESS
      </h2>
      <p style={{ fontFamily: FONT_BODY, color: COLORS.textMuted, fontSize: 14, marginBottom: 16, lineHeight: 1.6 }}>
        Click each step to see the exact commands and a plain-English explanation.
      </p>

      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        {steps.map((s, i) => {
          const isOpen = openStep === i;
          return (
            <div key={i} onClick={() => setOpenStep(isOpen ? null : i)} style={{
              background: isOpen ? `${s.color}11` : COLORS.bgCard,
              border: `1px solid ${isOpen ? s.color : COLORS.border}`,
              borderRadius: 8, padding: "12px 16px", cursor: "pointer",
              transition: "all 0.3s",
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <span style={{ fontFamily: FONT, fontSize: 11, color: COLORS.textDim, minWidth: 20 }}>{s.num}</span>
                <span style={{ fontSize: 18 }}>{s.icon}</span>
                <span style={{ fontFamily: FONT, fontSize: 13, color: s.color, fontWeight: 600, flex: 1 }}>{s.title}</span>
                <span style={{ fontSize: 12, color: COLORS.textDim, transform: isOpen ? "rotate(180deg)" : "rotate(0)", transition: "transform 0.3s" }}>▾</span>
              </div>
              {isOpen && (
                <div style={{ marginTop: 12 }}>
                  <div style={{
                    background: "#000", borderRadius: 6, padding: "10px 14px", marginBottom: 10,
                    fontFamily: FONT, fontSize: 11.5, color: COLORS.success, whiteSpace: "pre-wrap",
                    lineHeight: 1.7, border: `1px solid ${COLORS.border}`,
                  }}>
                    {s.cmd}
                  </div>
                  <p style={{ fontFamily: FONT_BODY, fontSize: 12.5, color: COLORS.textMuted, lineHeight: 1.65, margin: 0 }}>
                    💡 {s.explain}
                  </p>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// --- SECTION 5: GOLDEN RULES ---
function RulesSection() {
  const rules = [
    { icon: "🧠→⚙️", text: "Always upgrade Control Plane BEFORE worker nodes", color: COLORS.accent },
    { icon: "1️⃣", text: "Upgrade ONE minor version at a time (no skipping!)", color: COLORS.danger },
    { icon: "📦", text: "Upgrade kubeadm first, then kubelet & kubectl", color: COLORS.purple },
    { icon: "🚚", text: "Always DRAIN before upgrading a worker node", color: COLORS.warning },
    { icon: "✅", text: "Always UNCORDON after upgrading a worker node", color: COLORS.success },
    { icon: "📸", text: "Back up etcd BEFORE starting any upgrade", color: COLORS.cyan },
  ];

  return (
    <div style={{ marginBottom: 20 }}>
      <h2 style={{ fontFamily: FONT, fontSize: 18, color: COLORS.cyan, marginBottom: 6, letterSpacing: 2 }}>
        05 — GOLDEN RULES
      </h2>
      <p style={{ fontFamily: FONT_BODY, color: COLORS.textMuted, fontSize: 14, marginBottom: 16, lineHeight: 1.6 }}>
        Memorize these — they come up in CKA exams and real-world upgrades alike.
      </p>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
        {rules.map((r, i) => (
          <div key={i} style={{
            background: COLORS.bgCard, border: `1px solid ${COLORS.border}`,
            borderRadius: 8, padding: "12px 14px", display: "flex", alignItems: "center", gap: 10,
            borderLeft: `3px solid ${r.color}`,
          }}>
            <span style={{ fontSize: 20, minWidth: 28, textAlign: "center" }}>{r.icon}</span>
            <span style={{ fontFamily: FONT_BODY, fontSize: 12.5, color: COLORS.text, lineHeight: 1.5 }}>{r.text}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// --- SIDEBAR NAVIGATION ---
const NAV = [
  { id: "concepts",  icon: "🏗️", label: "The Big Picture" },
  { id: "analogy",   icon: "🏨", label: "Hotel Analogy" },
  { id: "simulator", icon: "🎬", label: "Upgrade Simulator" },
  { id: "process",   icon: "📋", label: "Step-by-Step Process" },
  { id: "rules",     icon: "📜", label: "Golden Rules" },
];

// --- MAIN EXPORT ---
export default function ClusterUpgrade() {
  const [active, setActive] = useState("concepts");

  const sections = {
    concepts: <ConceptSection />,
    analogy: <AnalogySection />,
    simulator: <SimulatorSection />,
    process: <ProcessSection />,
    rules: <RulesSection />,
  };

  return (
    <div style={layout.app}>
      <div style={layout.sidebar}>
        <div style={{ padding: "20px 16px 12px", borderBottom: `1px solid ${COLORS.border}` }}>
          <div style={{ fontSize: 11, color: COLORS.cyan, fontWeight: 700, textTransform: "uppercase", letterSpacing: 2, marginBottom: 4 }}>
            Kubernetes
          </div>
          <div style={{ fontSize: 15, fontWeight: 700, color: COLORS.text }}>Cluster Upgrade</div>
          <div style={{ display: "flex", gap: 4, marginTop: 8, flexWrap: "wrap" }}>
            {["kubeadm", "v1.29→30", "CKA"].map((t) => (
              <span key={t} style={layout.badge(COLORS.accent)}>{t}</span>
            ))}
          </div>
        </div>
        <nav style={{ padding: "10px 0" }}>
          {NAV.map((n) => (
            <div key={n.id} style={layout.navItem(active === n.id)} onClick={() => setActive(n.id)}>
              <span style={{ fontSize: 16 }}>{n.icon}</span>
              <span>{n.label}</span>
            </div>
          ))}
        </nav>
        <div style={{ padding: "12px 16px", borderTop: `1px solid ${COLORS.border}` }}>
          <div style={{ fontSize: 11, color: COLORS.textDim, lineHeight: 1.6 }}>
            Order:<br /><code style={{ color: COLORS.textMuted, fontFamily: FONT }}>control plane → workers</code>
          </div>
        </div>
      </div>
      <div style={layout.main}>{sections[active]}</div>
    </div>
  );
}
